import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import "@/index.css";
import App from "@/App";
import { initSentry } from "@/lib/sentry";
import SentryErrorFallback from "@/components/SentryErrorFallback";
import { queryClient } from "@/lib/query/queryClient";
import { persistOptions } from "@/lib/query/persister";

// Initialize Sentry as early as possible (no-op without REACT_APP_SENTRY_DSN).
initSentry();

// ──────────────────────────────────────────────────────────────────────
//  Voice-room diagnostic flag capture (Jun 29, 2026)
//  We need `?diag=1` to survive the login redirect. Auth navigations
//  strip the query string, so we persist the flag to localStorage the
//  moment the browser loads ANY page with `?diag=1` — before React
//  Router or the AuthContext can rewrite the URL. Read-only side
//  effect; anything not matching `1` / `0` is ignored.
//  Disable with `?diag=0`.
// ──────────────────────────────────────────────────────────────────────
(function captureVoiceDiagFlag() {
  try {
    const qs = new URLSearchParams(window.location.search);
    const val = qs.get("diag");
    if (val === "1" || (window.location.hash || "").includes("diag")) {
      window.localStorage.setItem("mesp_voice_diag", "1");
      console.info("[mesp] voice-room diagnostics enabled");
    } else if (val === "0") {
      window.localStorage.removeItem("mesp_voice_diag");
    }
  } catch (_e) { /* ignore */ }
})();

// ──────────────────────────────────────────────────────────────────────
//  Stale-chunk auto-recovery (Jun 2026)
//  After every Deploy webpack emits NEW hashed chunk filenames; any tab
//  that loaded the previous build still holds references to the OLD
//  filenames. The next code-split navigation then throws
//  `ChunkLoadError: Loading chunk N failed`. We catch that *globally*
//  and silently reload to pick up the fresh manifest — the user never
//  sees the red error screen.
//  A sessionStorage flag prevents an infinite reload loop if the chunk
//  is genuinely missing on the server (e.g. broken CDN).
// ──────────────────────────────────────────────────────────────────────
(function installChunkLoadRecovery() {
  const KEY = "mesp-chunk-reload-attempted";
  const RECOVERY_WINDOW_MS = 15_000;

  const isChunkErr = (msg) =>
    /Loading (?:CSS )?chunk [^\s]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg);

  const tryRecover = (reason) => {
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (last && Date.now() - last < RECOVERY_WINDOW_MS) {
      // Already reloaded recently — let the error bubble to the
      // boundary so the user sees the friendly message + button.
      return false;
    }
    sessionStorage.setItem(KEY, String(Date.now()));
    console.warn("[mesp] Reloading after chunk-load failure:", reason);
    const url = new URL(window.location.href);
    url.searchParams.set("_", Date.now().toString());
    window.location.replace(url.toString());
    return true;
  };

  window.addEventListener("error", (event) => {
    const msg = event?.message || event?.error?.message || "";
    if (isChunkErr(msg) || event?.error?.name === "ChunkLoadError") {
      if (tryRecover(msg)) event.preventDefault();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const msg = reason?.message || String(reason || "");
    if (isChunkErr(msg) || reason?.name === "ChunkLoadError") {
      if (tryRecover(msg)) event.preventDefault();
    }
  });
})();

// ──────────────────────────────────────────────────────────────────────
//  Offline-first data layer (Jul 2026)
//  The global QueryClient (stale-while-revalidate, 5 min staleTime /
//  24 h gcTime) now lives in `lib/query/queryClient.js` and its cache
//  is persisted to IndexedDB via `lib/query/persister.js` — only
//  queries declaring `meta: { persist: true }` are written to disk.
//  See /app/offline_architecture.md for the migration guide.
// ──────────────────────────────────────────────────────────────────────

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <App />
      </PersistQueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
